import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useProductPageStatus } from '../../ProductProvider';
import { IPageStatus } from '../../config/IPageStatus';
import css from './index.module.scss';
import { bundleIds, bundleIdsTeaser, BundleItemType } from './data';
import ProductItem from './ProductItem';
import { getSolarRecommand } from '@/service/solar-calculator';
import { useLang } from '@/i18n';

const Bundles: React.FC = () => {
    const { locale = '' } = useRouter();
    const pageStatus = useProductPageStatus();
    const IS_TEASER = pageStatus === IPageStatus.TEASER;
    const bundleId = IS_TEASER ? bundleIdsTeaser[locale] || [] : bundleIds[locale] || [];
    const [bundles, setBundles] = useState<BundleItemType[]>([]);
    const { t } = useLang('dpux.bundle');
    const productAll: { [key: string]: BundleItemType } = {};

    const getAsynItem = async (productId: string, variantId: string): Promise<BundleItemType> => {
        return await new Promise(async (resolve, reject) => {
            try {
                const res = await getSolarRecommand({ productId, variantId }, locale);
                if (res?.code === '0') {
                    productAll[`${productId}-${variantId}`] = res.data;
                    return resolve(res.data);
                } else {
                    console.error(res?.message);
                    return reject(res?.message);
                }
            } catch (e) {
                console.error(e);
                return reject(e);
            }
        });
    };

    const initBundleData = async () => {
        const temp1 = bundleId.filter((item: BundleItemType) => item?.productId && item?.variantId);

        if (pageStatus !== IPageStatus.TEASER) {
            const promiseAll: Promise<BundleItemType>[] = [];
            const promiseObj: { [key: string]: boolean } = {};
            temp1.forEach((item: BundleItemType) => {
                if (item?.productId && item?.variantId && !promiseObj[`${item.productId}-${item.variantId}`]) {
                    promiseAll.push(getAsynItem(item.productId, item.variantId));
                    promiseObj[`${item.productId}-${item.variantId}`] = true;
                }
                if (item?.subs && item?.subs?.length > 0) {
                    item.subs.forEach((subItem: BundleItemType) => {
                        if (subItem?.productId && subItem?.variantId && !promiseObj[`${subItem.productId}-${subItem.variantId}`]) {
                            promiseAll.push(getAsynItem(subItem.productId, subItem.variantId));
                            promiseObj[`${subItem.productId}-${subItem.variantId}`] = true;
                        }
                    });
                }
            });
            await Promise.all(promiseAll);
        }

        const cloneBundles = JSON.parse(JSON.stringify(bundleId));
        cloneBundles.forEach((item: BundleItemType, index: number) => {
            if (item?.productId && item?.variantId) {
                cloneBundles[index] = IS_TEASER
                    ? {
                          ...productAll[`${item.productId}-${item.variantId}`],
                          ...item,
                          power: t(`item${index + 1}_teaser_power`),
                          desc: t(`item${index + 1}_teaser_desc`),
                          productTitle: t(`item${index + 1}_teaser_title`),
                          quantity: 1,
                          inventoryQuantity: 1,
                      }
                    : {
                          ...productAll[`${item.productId}-${item.variantId}`],
                          ...item,
                          skuTitle: t(`item${index + 1}_title`) || item.skuTitle,
                          productTitle: t(`item${index + 1}_title`) || item.productTitle,
                          power: t(`item${index + 1}_power`),
                          desc: t(`item${index + 1}_desc`),
                          quantity: 1,
                          tip: t(`item${index + 1}_tip`),
                      };
            }

            if (cloneBundles[index]?.subs?.length > 0) {
                cloneBundles[index].subs.forEach((subItem: BundleItemType, subIndex: number) => {
                    if (subItem?.productId && subItem?.variantId) {
                        cloneBundles[index].subs[subIndex] = {
                            ...subItem,
                            ...productAll[`${subItem.productId}-${subItem.variantId}`],
                            quantity: 0,
                            parentVariantId: item.variantId,
                        };
                    }
                });
            }
        });
        setBundles(cloneBundles);
    };

    const handleCalc = (parentVariantId: string, variantId: string, type: 'plus' | 'minus') => {
        const [MIN, MAX] = [0, 100];
        const newBundles = JSON.parse(JSON.stringify(bundles));
        const targetParent: BundleItemType | undefined = newBundles.find((i: BundleItemType) => i.variantId === parentVariantId);
        if (targetParent && targetParent.subs && targetParent.subs?.length > 0) {
            const target: BundleItemType | undefined = targetParent.subs.find((i: BundleItemType) => i.variantId === variantId);
            if (target) {
                if (type === 'plus') {
                    if (target.inventoryPolicy === 'continue') {
                        target.quantity = Math.min((target?.quantity || 0) + 1, MAX);
                    } else {
                        target.quantity = Math.min((target?.quantity || 0) + 1, Math.min(target?.inventoryQuantity || 0, MAX));
                    }
                } else if (type === 'minus') {
                    target.quantity = Math.max((target.quantity || 0) - 1, MIN);
                }
            }
        }
        setBundles(newBundles);
    };

    useEffect(() => {
        initBundleData();
    }, [bundleId]);

    return (
        <section className={`${css['bundles']} bundles`}>
            <p className="title">{t('title')}</p>
            <div className={`bundles-box ${bundles?.length === 1 ? 'single' : ''}`}>
                {bundles.map((item: BundleItemType, k: number) => (
                    <ProductItem
                        key={`${`${item?.parentVariantId ? item?.parentVariantId + '-' : ''}`}${item.productId}-${item.variantId}-${k}`}
                        item={item}
                        callback={handleCalc}
                        isSingle={bundles?.length === 1}
                    />
                ))}
            </div>
        </section>
    );
};
export default Bundles;
